import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import moment from "moment"
import eye from "../../../assets/images/icons/eye.svg"
import { Link } from "react-router-dom"
import { Button } from "reactstrap"
import AddInvoiceModal from "modals/addInvoiceModal"
import {
  useApproveInvoiceStatusMutation,
  useLazyGetAllInvoicesQuery,
  useRejectInvoiceStatusMutation,
} from "store/api/reports/reports"
import AlertAccessModal from "modals/alertAccessModal"

import OutsideClickPopover from "modals/OutsideClickPopover"
import { getInvoiceStatus, getTableSortingHeaderIcon } from "helpers/common"
import RejectConfirmationModel from "./rejectConfirmationModel"
import ApproveConfirmationModel from "./approveConfirmationModel"

const defaultFilters = {
  page: 1,
  pageSize: 20,
}

const WPInvoiceManagementTable = () => {
  const [getAllInvoices, { isFetching }] = useLazyGetAllInvoicesQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isToast, setIsToast] = useState(false)
  const [openInvoice, setOpenInvoice] = useState(false)
  const [requestData, setRequestData] = useState(null)
  const [actionType, setActionType] = useState("")
  const [openAlertModal, setOpenAlertModal] = useState(false)
  const [onApprove, { isLoading: isApproving }] =
    useApproveInvoiceStatusMutation()
  const [onReject, { isLoading: isRejecting }] =
    useRejectInvoiceStatusMutation()
  document.title = "Invoice Management"

  useEffect(() => {
    fetchInvoices()
  }, [filters])

  const fetchInvoices = async () => {
    try {
      const payload = {
        ...filters,
        start_date: filters.created_at?.[0]
          ? moment(filters.created_at[0]).format("YYYY-MM-DD")
          : undefined,
        end_date: filters.created_at?.[1]
          ? moment(filters.created_at[1]).format("YYYY-MM-DD")
          : undefined,
      }
      delete payload.created_at
      if (!payload.status) delete payload.status

      const { data } = await getAllInvoices(payload).unwrap()
      setData(data?.rows || data || [])
      setCount(data?.count || (data?.rows?.length ?? 0))
    } catch (err) {
      console.error("Failed to fetch invoices:", err)
    }
  }

  const onRefresh = () => {
    setOpenInvoice(false)
    setFilters({ ...defaultFilters, timestamp: moment().unix() })
  }

  const columns = useMemo(
    () => [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        id: "created_at",
        accessorFn: (row) =>
          row?.created_at ? new Date(row.created_at).getTime() : null,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) =>
          moment(row.original.created_at).format("D MMM YYYY, h:mm A"),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Title"),
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row?.original?.title || "-",
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Code"),
        accessorKey: "code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original?.unique_code}</>,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>₹ {row?.original?.amount}</>,
      },
      {
        header: "File",
        accessorKey: "file",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <a
            href={row.original.file_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={eye} alt="View File" />
          </a>
        ),
      },
      {
        header: () => <div className="flex items-center gap-2">Status</div>,
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getInvoiceStatus(row?.original?.status)}
          </div>
        ),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { status, reason, id } = row.original

          if (status === "APPROVED") return null

          if (status === "REJECTED") {
            const tooltipId = `rejectInfoTooltip-${id}`

            return (
              <Link to="#" id={tooltipId} className="action-view">
                <i
                  className="mdi mdi-information-outline"
                  style={{ color: "#8F8C8C" }}
                />
                <OutsideClickPopover
                  target={tooltipId}
                  title="Reason for Rejection"
                  body={reason}
                />
              </Link>
            )
          }

          return (
            <div className="d-flex flex-column align-items-center gap-1 buttons">
              <Button
                className="approve"
                onClick={() => {
                  setIsToast(true)
                  setActionType("APPROVE")
                  setRequestData(row.original)
                }}
              >
                Approve
              </Button>

              <Button
                className="reject"
                size="sm"
                onClick={() => {
                  setIsToast(true)
                  setActionType("REJECT")
                  setRequestData(row.original)
                }}
              >
                Reject
              </Button>
            </div>
          )
        },
      },
    ],
    [isApproving]
  )

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />

      {openInvoice && (
        <AddInvoiceModal
          open={openInvoice}
          setOpen={setOpenInvoice}
          refresh={onRefresh}
        />
      )}
      {requestData && isToast && actionType === "APPROVE" && (
        <ApproveConfirmationModel
          open={isToast}
          data={{ id: requestData.id }}
          setOpen={setIsToast}
          isLoading={isApproving}
          onClose={() => {
            setIsToast(false)
            setActionType("")
          }}
          onApprove={onApprove}
          onSuccess={fetchInvoices}
        />
      )}

      {requestData && isToast && actionType === "REJECT" && (
        <RejectConfirmationModel
          open={isToast}
          data={{ id: requestData.id }}
          setOpen={setIsToast}
          isLoading={isRejecting}
          onClose={() => {
            setIsToast(false)
            setActionType("")
          }}
          onReject={onReject}
          onSuccess={fetchInvoices}
        />
      )}

      {openAlertModal && (
        <AlertAccessModal
          open={openAlertModal}
          onClose={() => setOpenAlertModal(false)}
        />
      )}
    </>
  )
}

export default WPInvoiceManagementTable
